/**
 * useChallenge.js
 * Handles the on-chain + backend flow for creating and joining 1v1 challenges.
 */
import { useState } from 'react';
import { useActiveAccount } from 'thirdweb/react';
import { getContract, prepareContractCall, sendTransaction, waitForReceipt, readContract } from 'thirdweb';
import { polygon } from 'thirdweb/chains';
import { client } from '../thirdwebClient';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Converts raw thirdweb/viem/ethers errors into short human-readable strings.
 */
function parseChainError(err) {
  // Backend HTTP error — already cleaned server-side
  if (err?.response?.data?.error) return err.response.data.error;

  // viem / thirdweb structured fields (preferred over raw message)
  if (err?.shortMessage) return err.shortMessage;

  const raw = err?.message || String(err) || '';

  // ethers v6 dumps the full transaction blob in err.message — strip it
  // Format: `... (transaction="0x...", info=..., code=..., version=...)`
  // Also viem: `{transaction="0x..."}`
  const isRawTxBlob = /[\(\{]transaction\s*=\s*["']0x[0-9a-f]+/i.test(raw);

  // Insufficient gas / native token
  if (/insufficient funds/i.test(raw))
    return 'Not enough POL for gas. Add ~$0.50 of POL to your wallet.';

  // User rejected in wallet
  if (/user rejected|user denied|denied transaction|cancelled by user/i.test(raw))
    return 'Transaction cancelled in wallet.';

  // USDC transfer failed (allowance or balance issue)
  if (/transfer failed|transferFrom/i.test(raw))
    return 'USDC transfer failed — check your USDC balance and approval.';

  // Contract revert with reason string
  const revertMatch = raw.match(/reverted.*?["']([^"']+)["']/i)
    || raw.match(/reason="([^"]+)"/)
    || raw.match(/revert\s+(.{3,60})/i);
  if (revertMatch) return `Transaction reverted: ${revertMatch[1]}`;

  // Execution reverted (no reason)
  if (/execution reverted/i.test(raw))
    return 'Transaction reverted on-chain. Check USDC balance and allowance.';

  // Timeout
  if (/timeout|timed out/i.test(raw))
    return 'Transaction timed out. The network may be congested — try again.';

  // Network errors
  if (/network|fetch|ECONN|ENOTFOUND/i.test(raw))
    return 'Network error — check your connection and try again.';

  // Raw tx blob with no recognizable signal: don't show it
  if (isRawTxBlob) return 'Transaction failed. Check your wallet for details.';

  // Generic fallback
  if (raw.length > 120) return 'Transaction failed. Check your wallet for details.';
  return raw || 'Failed to create challenge';
}

export function useCreateChallenge() {
  const account = useActiveAccount();
  const [state, setState] = useState({ step: 'idle', error: null });

  async function createChallenge({ game, betAmountUsdc, usdcAddress, gamePlayerId, gameField }) {
    if (!account) throw new Error('Please connect your wallet first');

    try {
      // Step 1: deploy escrow on backend
      setState({ step: 'deploying', error: null });
      const betAmount = String(Math.round(betAmountUsdc * 1_000_000)); // convert $ → micro-USDC

      const { data: initData } = await axios.post(`${API}/challenge/init`, {
        game,
        betAmount,
        usdcAddress,
        walletAddress: account.address,
        gamePlayerId,
        gameField,
      }, { timeout: 60_000 });

      const { challengeId, contractAddress } = initData;

      // Step 2: approve USDC
      setState({ step: 'approving', error: null });
      const usdcContract = getContract({ client, chain: polygon, address: usdcAddress });
      const approveTx = prepareContractCall({
        contract: usdcContract,
        method:   'function approve(address spender, uint256 amount) returns (bool)',
        params:   [contractAddress, BigInt(betAmount)],
      });
      const approveResult = await sendTransaction({ transaction: approveTx, account });
      await waitForReceipt({ client, chain: polygon, transactionHash: approveResult.transactionHash });

      // Step 3: join (fund) the escrow
      setState({ step: 'funding', error: null });
      const escrowContract = getContract({ client, chain: polygon, address: contractAddress });
      const joinTx = prepareContractCall({
        contract: escrowContract,
        method:   'function joinTournament()',
        params:   [],
      });
      const joinResult = await sendTransaction({ transaction: joinTx, account });
      await waitForReceipt({ client, chain: polygon, transactionHash: joinResult.transactionHash });

      // Step 4: confirm with backend
      setState({ step: 'confirming', error: null });
      const { data: confirmData } = await axios.post(`${API}/challenge/${challengeId}/confirm`, {
        txHash:        joinResult.transactionHash,
        walletAddress: account.address,
      }, { timeout: 30_000 });

      setState({ step: 'done', error: null });
      return { challengeId, challenge: confirmData.challenge };
    } catch (err) {
      const msg = parseChainError(err);
      setState({ step: 'error', error: msg });
      throw err;
    }
  }

  function reset() { setState({ step: 'idle', error: null }); }
  return { createChallenge, state, reset };
}

export function useJoinChallenge() {
  const account = useActiveAccount();
  const [state, setState] = useState({ step: 'idle', error: null });

  async function joinChallenge({ challenge, gamePlayerId, gameField }) {
    if (!account) throw new Error('Please connect your wallet first');

    try {
      const usdcAddress = await readContract({
        contract: getContract({ client, chain: polygon, address: challenge.contract_address }),
        method:   'function usdc() returns (address)',
        params:   [],
      });

      // Step 1: approve USDC
      setState({ step: 'approving', error: null });
      const usdcContract = getContract({ client, chain: polygon, address: usdcAddress });
      const approveTx = prepareContractCall({
        contract: usdcContract,
        method:   'function approve(address spender, uint256 amount) returns (bool)',
        params:   [challenge.contract_address, BigInt(challenge.bet_amount)],
      });
      const approveResult = await sendTransaction({ transaction: approveTx, account });
      await waitForReceipt({ client, chain: polygon, transactionHash: approveResult.transactionHash });

      // Step 2: join escrow on-chain
      setState({ step: 'joining', error: null });
      const escrowContract = getContract({ client, chain: polygon, address: challenge.contract_address });
      const joinTx = prepareContractCall({
        contract: escrowContract,
        method:   'function joinTournament()',
        params:   [],
      });
      const joinResult = await sendTransaction({ transaction: joinTx, account });
      await waitForReceipt({ client, chain: polygon, transactionHash: joinResult.transactionHash });

      // Step 3: record with backend
      setState({ step: 'confirming', error: null });
      const { data } = await axios.post(`${API}/challenge/${challenge.id}/join`, {
        walletAddress: account.address,
        txHash:        joinResult.transactionHash,
        gamePlayerId,
        gameField,
      }, { timeout: 30_000 });

      setState({ step: 'done', error: null });
      return data;
    } catch (err) {
      const msg = parseChainError(err);
      setState({ step: 'error', error: msg });
      throw err;
    }
  }

  function reset() { setState({ step: 'idle', error: null }); }
  return { joinChallenge, state, reset };
}
